import { isSharedEmployeeProfile } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/helpers/accessLevelFilters";
import type { ProfileDto } from "app/pages/sales-intelligence/sub-modules/contacts-search-profiles/types/dto";

export type SplitEmployeeProfilesResult = {
    sharedRows: ProfileDto[];
    privateRows: ProfileDto[];
};

/**
 * Type, Updated and the creator name are deliberately excluded from search: only the targeting criteria
 * and the profile name are searchable.
 */
const getSearchableValues = (profile: ProfileDto): string[] => [
    profile.name ?? "",
    ...profile.departments,
    ...profile.seniority,
    ...profile.includeTitles,
    ...profile.excludeTitles,
];

const matchesSearch = (profile: ProfileDto, search: string): boolean => {
    if (!search) {
        return true;
    }

    return getSearchableValues(profile).some((value) => value.toLowerCase().includes(search));
};

export const getEmployeeProfileUpdatedDate = (profile: ProfileDto): string | undefined =>
    profile.updatedTime ?? profile.createdTime;

const getUpdatedTimestamp = (profile: ProfileDto): number => {
    const rawDate = getEmployeeProfileUpdatedDate(profile);
    if (!rawDate) {
        return 0;
    }

    const timestamp = Date.parse(rawDate);
    return Number.isNaN(timestamp) ? 0 : timestamp;
};

const compareByUpdatedDesc = (a: ProfileDto, b: ProfileDto): number => getUpdatedTimestamp(b) - getUpdatedTimestamp(a);

export const countEmployeeProfilesBySegment = (profiles: readonly ProfileDto[]) => {
    let sharedCount = 0;
    for (const profile of profiles) {
        if (isSharedEmployeeProfile(profile)) {
            sharedCount++;
        }
    }
    return { sharedCount, privateCount: profiles.length - sharedCount };
};

export const splitEmployeeProfiles = (
    profiles: readonly ProfileDto[],
    searchTerm: string,
): SplitEmployeeProfilesResult => {
    const trimmedSearch = searchTerm.trim().toLowerCase();
    const sharedRows: ProfileDto[] = [];
    const privateRows: ProfileDto[] = [];

    profiles.forEach((profile) => {
        if (!matchesSearch(profile, trimmedSearch)) {
            return;
        }
        (isSharedEmployeeProfile(profile) ? sharedRows : privateRows).push(profile);
    });

    sharedRows.sort(compareByUpdatedDesc);
    privateRows.sort(compareByUpdatedDesc);

    return { sharedRows, privateRows };
};
